"use client";

import Image from "next/image";

import ScrollReveal from "@/components/animations/ScrollReveal";
import MobileAppButton from "@/components/cards/MobileAppButton";
import { TrackedNativeButton } from "@/components/analytics/TrackedCtaButton";
import { APPLE_APP_STORE_LINK, GOOGLE_APP_STORE_LINK } from "@/lib/constant";

import AppleIcon from "@/../public/assets/home/downloadApp/apple.svg";
import GoogleIcon from "@/../public/assets/home/downloadApp/google.svg";
import mobile from "@/../public/assets/home/downloadApp/mobile-app.png";

type Props = {
	/** GA4 location for store + Start today CTAs on this block. */
	ctaLocation?: string;
};

export default function DownloadApp({ ctaLocation = "download_app_section" }: Props) {
	return (
		<section className="mobile-app-gradient-background py-10 md:py-16 lg:pb-28 lg:pt-32">
			<div className="container grid gap-8 p-8 lg:min-h-[500px] lg:grid-cols-2 lg:items-center lg:gap-0">
				<ScrollReveal direction="up" delay={0} className="order-1 lg:hidden">
					<div>
						<TrackedNativeButton
							ctaId="start_today_download_app_mobile"
							ctaName="Start today"
							ctaLocation={ctaLocation}
							className="mb-4 w-fit rounded-full bg-[#E4FBFD] px-6 py-2 text-sm font-medium text-foreground transition-colors hover:bg-cream-hover dark:!bg-core-midnight dark:text-foreground dark:hover:!bg-core-deep-sea"
						>
							Start today
						</TrackedNativeButton>
						<h2 className="text-2.5xl font-semibold">
							Download <span className="text-tertiary">Techxeed</span> App
						</h2>
					</div>
				</ScrollReveal>

				<ScrollReveal direction="up" delay={100} className="order-2 lg:order-1">
					<div className="flex flex-col items-center lg:items-start">
						<TrackedNativeButton
							ctaId="start_today_download_app_desktop"
							ctaName="Start today"
							ctaLocation={ctaLocation}
							className="mb-4 hidden w-fit rounded-full bg-[#E4FBFD] px-6 py-2 text-sm font-medium text-foreground transition-colors hover:bg-cream-hover dark:!bg-core-midnight dark:text-foreground dark:hover:!bg-core-deep-sea lg:mb-6 lg:block"
						>
							Start today
						</TrackedNativeButton>
						<h2 className="hidden text-2.5xl font-semibold lg:block 2xl:text-3xl">
							Download <span className="text-tertiary">Techxeed</span> App
						</h2>
						<p className="my-6 text-sm font-light leading-snug">
							Get your work done on the go with the Techxeed app. Place orders and
							interact with our team from wherever you are in the world
						</p>

						<div className="grid w-full flex-1 items-end gap-x-4 gap-y-2 sm:mx-auto sm:max-w-lg sm:grid-cols-2 lg:mx-0 lg:gap-x-8">
							<MobileAppButton
								size="lg"
								href={APPLE_APP_STORE_LINK}
								store="Apple Store"
								iconLight={AppleIcon}
								labelTop="Download on"
								background="light"
								ctaLocation={ctaLocation}
							/>
							<MobileAppButton
								size="lg"
								href={GOOGLE_APP_STORE_LINK}
								store="Google Play"
								iconLight={GoogleIcon}
								labelTop="Get in on"
								background="light"
								ctaLocation={ctaLocation}
							/>
						</div>
					</div>
				</ScrollReveal>

				<ScrollReveal direction="left" delay={150} className="order-1 flex justify-center lg:order-2 lg:items-end lg:justify-end lg:self-end" aria-hidden>
					<Image
						src={mobile}
						alt="Techxeed mobile app preview"
						className="max-w-64 lg:max-w-sm xl:max-w-md"
						sizes="(max-width: 1024px) 256px, (max-width: 1280px) 384px, 448px"
						loading="lazy"
					/>
				</ScrollReveal>
			</div>
		</section>
	);
}
